import type { Block, ChildNode } from '@gitbook/slate';

import type { BlockDiffChangeType } from '../types';

/*
 * To avoid nodes with duplicated keys in documents that represent diffs,
 * we generate new keys for certain changes (like deletions).
 * An example scenario is a node moved from one location to another, that will be marked as deleted in the original location,
 * and added in the new location.
 *
 * But we need to keep track of the original keys to be able to match them with the original nodes.
 */

const diffKeyCounters = new Map<string, number>();
let diffKeyScopeDepth = 0;

/**
 * Ensure diff key counters stay local to the diff operation.
 * We reset counters at the root scope so each diff produces deterministic keys,
 * while nested scopes reuse the same counters to keep related additions/deletions in sync.
 */
export function withDiffKeyScope<T>(fn: () => T): T {
    const isRootScope = diffKeyScopeDepth === 0;

    if (isRootScope) {
        diffKeyCounters.clear();
    }

    diffKeyScopeDepth += 1;

    try {
        return fn();
    } finally {
        diffKeyScopeDepth -= 1;
        if (isRootScope) {
            diffKeyCounters.clear();
        }
    }
}

/**
 * Get the original key from a diff key.
 */
export function getOriginalDiffKey(diffKey: string): string {
    const parsed = parseDiffKey(diffKey);
    if (!parsed) {
        return diffKey;
    }

    return parsed.originalKey;
}

/**
 * Parse a diff key to extract the original key.
 * It returns `primary` as true if the key is the first occurence of a node represented multiple times in the diff.
 */
export function parseDiffKey(
    diffKey: string
): null | { originalKey: string; change: BlockDiffChangeType; primary: boolean } {
    const match = diffKey.match(/^diff:(\S+)-(.+?)(?:~(\d+))?$/);

    if (!match) {
        return null;
    }

    return {
        originalKey: match[2],
        change: match[1] as BlockDiffChangeType,
        primary: match[3] === undefined,
    };
}

/**
 * Get the diff key for a node to transform.
 */
export function getDiffKeyForTransform(nodeKey: string, change: BlockDiffChangeType): string {
    const originalKey = getOriginalDiffKey(nodeKey);

    const newKey = getUniqueDiffKey(change, originalKey);
    return newKey;
}

/**
 * Transform the key of a node to a diff key.
 */
export function transformDiffKey<T extends ChildNode>(node: T, change: BlockDiffChangeType): T {
    // @ts-ignore - slate typing
    return node.merge({ key: getDiffKeyForTransform(node.key, change) });
}

/**
 * Transform the key of the node and all its descendants to a diff key.
 */
export function transformDeepDiffKey(node: Block, change: BlockDiffChangeType): Block {
    return transformDiffKey(node, change).mapDescendants((n) => transformDiffKey(n, change));
}

function getCounterKey(change: BlockDiffChangeType, originalKey: string): string {
    return `${change}:${originalKey}`;
}

function getUniqueDiffKey(change: BlockDiffChangeType, originalKey: string): string {
    if (diffKeyScopeDepth === 0) {
        throw new Error(
            `Cannot generate a diff key for a node  when not wrapped in withDiffKeyScope. Use 'getBaseDiffKey' to get the base diff key.`
        );
    }

    const baseKey = getBaseDiffKey(change, originalKey);
    const counterKey = getCounterKey(change, originalKey);
    const count = diffKeyCounters.get(counterKey) ?? 0;

    diffKeyCounters.set(counterKey, count + 1);

    if (count === 0) {
        return baseKey;
    }

    return `${baseKey}~${count}`;
}

/**
 * Get the base diff key for a node.
 */
export function getBaseDiffKey(change: BlockDiffChangeType, originalKey: string): string {
    return `diff:${change}-${originalKey}`;
}
